import React, { useEffect, useState } from 'react';
import socialAuthService, { SocialProvider } from 'api/services/socialAuth.service';

/**
 * Continue with Google / Microsoft 365 / ChatGPT.
 *
 * <h3>Links, not script</h3>
 * Each button is a full page navigation to the server, which runs one
 * authorization-code + PKCE flow for every provider. No vendor SDK loads here, so the
 * login page has no third-party script in it and nothing to keep up to date.
 *
 * <h3>Renders nothing when the feature is off</h3>
 * The endpoint returns an empty list unless providers are configured, and a failed
 * request is treated the same way. Password sign-in is the path that must never be
 * blocked by this component.
 */

/** Brand marks, inline so no icon request can fail or leak a referrer. */
const MARKS: Record<string, React.ReactNode> = {
  GOOGLE: (
    <svg viewBox="0 0 18 18" className="h-[18px] w-[18px]" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  ),
  MICROSOFT: (
    <svg viewBox="0 0 18 18" className="h-[18px] w-[18px]" aria-hidden="true">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  ),
  OPENAI: (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden="true" fill="currentColor">
      <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.98 4.18a5.98 5.98 0 0 0-4 2.9 6.05 6.05 0 0 0 .75 7.1 5.98 5.98 0 0 0 .51 4.91 6.05 6.05 0 0 0 6.52 2.9A5.98 5.98 0 0 0 13.26 24a6.05 6.05 0 0 0 5.77-4.19 5.98 5.98 0 0 0 4-2.9 6.05 6.05 0 0 0-.75-7.09Zm-9.02 12.6a4.48 4.48 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.78.78 0 0 0 .39-.68v-6.74l2.02 1.17a.07.07 0 0 1 .04.05v5.58a4.5 4.5 0 0 1-4.49 4.5ZM3.6 18.3a4.47 4.47 0 0 1-.54-3.01l.14.09 4.78 2.76a.78.78 0 0 0 .78 0l5.84-3.37v2.33a.08.08 0 0 1-.03.06l-4.83 2.79a4.5 4.5 0 0 1-6.14-1.65ZM2.34 7.9a4.48 4.48 0 0 1 2.35-1.97v5.68a.77.77 0 0 0 .38.67l5.82 3.36-2.02 1.17a.07.07 0 0 1-.07 0L3.97 14a4.5 4.5 0 0 1-1.63-6.1Zm16.6 3.86-5.84-3.4L15.1 7.2a.07.07 0 0 1 .07 0l4.83 2.79a4.49 4.49 0 0 1-.68 8.1v-5.68a.78.78 0 0 0-.39-.66Zm2.01-3.03-.14-.08-4.77-2.78a.78.78 0 0 0-.79 0L9.42 9.24V6.9a.07.07 0 0 1 .03-.06l4.83-2.79a4.49 4.49 0 0 1 6.67 4.65ZM8.32 12.87 6.3 11.7a.07.07 0 0 1-.04-.06V6.07a4.49 4.49 0 0 1 7.37-3.44l-.14.08-4.78 2.76a.78.78 0 0 0-.39.68v6.72Zm1.1-2.36 2.6-1.5 2.61 1.5v3l-2.6 1.5-2.61-1.5v-3Z" />
    </svg>
  ),
};

interface Props {
  /** "signin" or "signup" — only changes the wording. */
  intent?: 'signin' | 'signup';
}

const SocialSignInButtons: React.FC<Props> = ({ intent = 'signin' }) => {
  const [providers, setProviders] = useState<SocialProvider[]>([]);

  useEffect(() => {
    let cancelled = false;
    socialAuthService.providers().then((list) => {
      if (!cancelled) setProviders(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (providers.length === 0) return null;

  const verb = intent === 'signup' ? 'Sign up' : 'Continue';

  return (
    <div className="mt-5">
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs text-slate-500">or</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {providers.map((p) => (
          <a
            key={p.provider}
            href={socialAuthService.startUrl(p.provider)}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            {MARKS[p.provider] ?? null}
            <span>
              {verb} with {p.display_name}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};

export default SocialSignInButtons;
